import { useMemo } from 'react';
import { Layout, Menu, Breadcrumb, Avatar, Dropdown, Button, theme } from 'antd';
import type { MenuProps, BreadcrumbProps } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  UserOutlined,
  ReadOutlined,
  FileTextOutlined,
  TeamOutlined,
  BellOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ROLE } from '@network-verify/shared';
import { useAuthStore } from '../store/auth';
import { useUiStore } from '../store/ui';

const { Header, Sider, Content } = Layout;

/** 路径段 → 中文名映射（用于面包屑） */
const NAME_MAP: Record<string, string> = {
  dashboard: '仪表盘',
  apps: '应用管理',
  logs: '操作日志',
  tutorial: '使用教程',
  profile: '个人中心',
  admin: '管理后台',
  users: '用户管理',
  api: 'WebApi',
  variables: '变量数据',
  algorithms: '远程算法',
  blacklist: '黑名单',
  versions: '版本管理',
  keys: '卡密管理',
  generate: '生成卡密',
  query: '制卡查询',
  devices: '设备列表',
  online: '在线设备',
};

export default function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const siderCollapsed = useUiStore((s) => s.siderCollapsed);
  const toggleSider = useUiStore((s) => s.toggleSider);
  const { token: antdToken } = theme.useToken();

  /** 根据角色生成侧边菜单 */
  const menuItems = useMemo<NonNullable<MenuProps['items']>>(() => {
    const items: NonNullable<MenuProps['items']> = [
      { key: '/dashboard', icon: <DashboardOutlined />, label: '仪表盘' },
      { key: '/apps', icon: <AppstoreOutlined />, label: '应用管理' },
      { key: '/logs', icon: <FileTextOutlined />, label: '操作日志' },
      { key: '/tutorial', icon: <ReadOutlined />, label: '使用教程' },
    ];
    if (user?.role === ROLE.ADMIN) {
      items.push({ key: '/admin/users', icon: <TeamOutlined />, label: '用户管理' });
    }
    return items;
  }, [user]);

  /** 面包屑 */
  const breadcrumbItems = useMemo<NonNullable<BreadcrumbProps['items']>>(() => {
    const snippets = location.pathname.split('/').filter(Boolean);
    const items: NonNullable<BreadcrumbProps['items']> = [{ title: '首页' }];
    snippets.forEach((s) => {
      items.push({ title: NAME_MAP[s] ?? s });
    });
    return items;
  }, [location.pathname]);

  /** 当前选中菜单项（按顶级路径匹配） */
  const selectedKey = useMemo(() => {
    const top = '/' + (location.pathname.split('/')[1] ?? '');
    if (top === '/admin') return '/admin/users';
    return top;
  }, [location.pathname]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
  };

  const handleUserMenuClick: MenuProps['onClick'] = async ({ key }) => {
    if (key === 'logout') {
      await logout();
      navigate('/login', { replace: true });
    } else {
      navigate(key);
    }
  };

  const userMenuItems: MenuProps['items'] = [
    { key: '/profile', icon: <UserOutlined />, label: '个人中心' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={siderCollapsed}
        theme="light"
        style={{ boxShadow: '2px 0 8px rgba(0,0,0,0.06)' }}
      >
        <div
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: antdToken.colorPrimary,
            fontWeight: 700,
            fontSize: siderCollapsed ? 16 : 18,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {siderCollapsed ? 'NV' : '网络验证系统'}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>

      <Layout>
        <Header
          style={{
            background: antdToken.colorBgContainer,
            padding: '0 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              type="text"
              icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={toggleSider}
              aria-label="切换侧边栏"
            />
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button type="text" icon={<BellOutlined />} aria-label="通知" />
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  padding: '0 4px',
                }}
              >
                <Avatar icon={<UserOutlined />} />
                <span>{user?.username ?? '未登录'}</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content
          style={{
            margin: 16,
            padding: 24,
            background: antdToken.colorBgContainer,
            borderRadius: 8,
            minHeight: 280,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
